import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import DistributionGridApi from "../../../../../infrastructure/api/distributionGrid";
import { formatLocalDateForApi, parseApiLocalDateTime } from "../../../../middleware/middleware";
import { pick } from "../../useDistributionPortalContext";

/** Mirrors the backend byte enums (substationKind, feederKind, ownershipKind, lvTransInstallationKind). */
export const SUBSTATION_KINDS = [
  { value: 0, title: "ثابت" },
  { value: 1, title: "سیار" },
];

export const FEEDER_KINDS = [
  { value: 0, title: "داخلی" },
  { value: 1, title: "بیرونی" },
  { value: 2, title: "خازنی" },
];

export const OWNERSHIP_KINDS = [
  { value: 0, title: "عمومی" },
  { value: 1, title: "اختصاصی" },
];

export const INSTALLATION_KINDS = [
  { value: 0, title: "هوایی" },
  { value: 1, title: "زمینی" },
  { value: 2, title: "کمپکت" },
];

export const TRANS_NUMBERS = [1, 2, 3, 4, 5, 6];

export const listOf = (res) => {
  const list = res?.data ?? res?.Data ?? [];
  return Array.isArray(list) ? list : [];
};

export const toNumberOrNull = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const toFieldValue = (value) => (value == null ? "" : String(value));

export const toDateValue = (value) => parseApiLocalDateTime(value);

export const toApiDate = (value) => formatLocalDateForApi(value);

export const normalizeLookup = (item) => ({
  id: Number(pick(item, "id", "Id")),
  title: pick(item, "title", "Title") || "—",
  parentId: pick(item, "parentId", "ParentId") ?? null,
});

const emptyLookups = { areas: [], cities: [], substations: [], feeders: [] };

/** Areas, cities, substations and feeders of the selected distribution company. */
export const useGridLookups = (companyId, enabled) => {
  const [lookups, setLookups] = useState(emptyLookups);

  const reloadLookups = useCallback(async () => {
    if (!companyId || !enabled) {
      setLookups(emptyLookups);
      return;
    }
    try {
      const res = await DistributionGridApi.getLookups(companyId);
      const data = res?.data ?? res?.Data ?? {};
      setLookups({
        areas: (pick(data, "areas", "Areas") || []).map(normalizeLookup),
        cities: (pick(data, "cities", "Cities") || []).map(normalizeLookup),
        substations: (pick(data, "substations", "Substations") || []).map(normalizeLookup),
        feeders: (pick(data, "feeders", "Feeders") || []).map(normalizeLookup),
      });
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت اطلاعات پایه شبکه");
    }
  }, [companyId, enabled]);

  useEffect(() => {
    reloadLookups();
  }, [reloadLookups]);

  const titleById = useMemo(() => {
    const build = (items) => new Map(items.map((x) => [x.id, x.title]));
    return {
      area: build(lookups.areas),
      substation: build(lookups.substations),
    };
  }, [lookups]);

  return { lookups, reloadLookups, titleById };
};

/** DeleteConfirm expects {isSuccess, message}; the API layer throws on failure. */
export const runDelete = async (action, successMessage) => {
  try {
    const res = await action();
    return { isSuccess: true, message: res?.message || res?.Message || successMessage };
  } catch (e) {
    return { isSuccess: false, message: e?.message || "خطا در حذف" };
  }
};
